import { supabase } from '../lib/supabase';

/**
 * Normalize a course name for comparison by removing parenthetical ID suffixes
 * e.g., "Dunscar Golf Club (1005468)" -> "dunscar golf club"
 * @param {string} name - Course name to normalize
 * @returns {string} - Normalized name (lowercase, no ID suffix)
 */
const normalizeCourseNameForComparison = (name) => {
  if (!name) return '';
  // Remove parenthetical suffixes that look like IDs (numbers only)
  // e.g., "(1005468)" or "( 12345 )"
  return name
    .replace(/\s*\(\s*\d+\s*\)\s*$/, '') // Remove trailing (number)
    .toLowerCase()
    .trim();
};

/**
 * Check if a course is a duplicate based on google_place_id, external_id, OR name
 * @param {Object} course - Course to check
 * @param {Array} existingCourses - Array of existing courses
 * @returns {Object|null} - The matching existing course, or null if no duplicate
 */
const findDuplicateCourse = (course, existingCourses) => {
  // First check by google_place_id if available
  if (course.google_place_id) {
    const matchByPlaceId = existingCourses.find(existing =>
      existing.google_place_id && existing.google_place_id === course.google_place_id
    );
    if (matchByPlaceId) return matchByPlaceId;
  }

  // Also check by normalized name (case-insensitive, without ID suffixes)
  if (course.name) {
    const normalizedCourseName = normalizeCourseNameForComparison(course.name);
    const matchByName = existingCourses.find(existing =>
      existing.name && normalizeCourseNameForComparison(existing.name) === normalizedCourseName
    );
    if (matchByName) return matchByName;
  }

  return null;
};

/**
 * Save external courses to the database (batch upsert)
 * Uses Google Places data directly (no OpenAI standardisation needed)
 * @param {Array} courses - Array of course objects from Google Places API
 * @returns {Object} - Result object with saved data or error
 */
export const saveExternalCoursesToDatabase = async (courses) => {
  if (!courses || courses.length === 0) {
    return { data: [], error: null };
  }

  try {
    // Step 1: Get existing courses from database for deduplication
    const courseNames = courses.map(c => c.name?.toLowerCase()).filter(Boolean);
    const uniqueNames = [...new Set(courseNames)];

    let existingCourses = [];
    if (uniqueNames.length > 0) {
      // Fetch potential duplicates
      const { data: potentialDuplicates, error: fetchError } = await supabase
        .from('golf_clubs')
        .select('id, google_place_id, name, country, country_code, latitude, longitude');

      if (fetchError) {
        console.warn('Error fetching existing courses:', fetchError);
      } else {
        existingCourses = potentialDuplicates || [];
      }
    }

    // Step 2: Filter out duplicates and identify new courses
    const newCourses = [];
    const duplicateMap = new Map(); // google_place_id -> existing course

    for (const course of courses) {
      // First check by google_place_id
      const existingByPlaceId = existingCourses.find(
        e => e.google_place_id && e.google_place_id === course.google_place_id
      );

      if (existingByPlaceId) {
        duplicateMap.set(course.google_place_id, existingByPlaceId);
        continue;
      }

      // Check by exact name match (case-insensitive) - prevents unique constraint violation
      const existingByName = existingCourses.find(
        e => e.name && e.name.toLowerCase() === course.name?.toLowerCase()
      );
      if (existingByName) {
        duplicateMap.set(course.google_place_id, existingByName);
        continue;
      }

      // Then check by name + country + distance
      const duplicate = findDuplicateCourse(course, existingCourses);
      if (duplicate) {
        duplicateMap.set(course.google_place_id, duplicate);
        continue;
      }

      // Also check against courses we're about to insert (by name)
      const duplicateByNameInBatch = newCourses.find(
        n => n.name && n.name.toLowerCase() === course.name?.toLowerCase()
      );
      if (duplicateByNameInBatch) {
        duplicateMap.set(course.google_place_id, duplicateByNameInBatch);
        continue;
      }

      // Also check against courses we're about to insert (by other criteria)
      const duplicateInBatch = findDuplicateCourse(course, newCourses);
      if (duplicateInBatch) {
        duplicateMap.set(course.google_place_id, duplicateInBatch);
        continue;
      }

      newCourses.push(course);
    }

    if (newCourses.length === 0) {
      // All courses already exist, return with existing IDs
      const mergedCourses = courses.map(course => {
        const existing = duplicateMap.get(course.google_place_id);
        if (existing) {
          return { ...course, id: existing.id, source: 'database' };
        }
        return course;
      });
      return { data: mergedCourses, error: null };
    }

    // Step 3: Prepare and insert new courses with all available fields
    const coursesToInsert = newCourses.map(course => ({
      // Core identification
      name: course.name,
      google_place_id: course.google_place_id,
      display_name: course.display_name,
      // Location
      latitude: course.latitude,
      longitude: course.longitude,
      address: course.address,
      city: course.city,
      state: course.state,
      country: course.country,
      country_code: course.country_code,
      postcode: course.postcode,
      plus_code: course.plus_code,
      short_formatted_address: course.short_formatted_address,
      // Contact & Web
      phone: course.phone,
      website: course.website,
      google_maps_uri: course.google_maps_uri,
      // Ratings & Reviews
      rating: course.rating,
      user_rating_count: course.user_rating_count,
      editorial_summary: course.editorial_summary,
      review_summary: course.review_summary,
      reviews: course.reviews,
      // Opening Hours
      regular_opening_hours: course.regular_opening_hours,
      // Business Info
      business_status: course.business_status,
      primary_type: course.primary_type,
      types: course.types,
      // Media
      photos: course.photos,
      // Source
      source: 'google_places'
    }));

    // Plain insert - the legacy UNIQUE(name) constraint was dropped to allow
    // multi-course rows (Belfry - Brabazon vs Belfry - PGA National). The
    // dedupe pass above already filters anything that matches an existing row
    // by google_place_id, name, or location, so duplicates here are rare.
    const { data: insertedCourses, error: insertError } = await supabase
      .from('golf_clubs')
      .insert(coursesToInsert)
      .select();

    if (insertError) {
      console.error('Error upserting external courses:', insertError);
      return { data: courses, error: insertError };
    }

    // Step 4: Merge all results
    const insertedMap = new Map(
      (insertedCourses || []).map(c => [c.google_place_id, c])
    );

    const mergedCourses = courses.map(course => {
      const inserted = insertedMap.get(course.google_place_id);
      if (inserted) {
        return { ...course, id: inserted.id, source: 'database' };
      }

      const existing = duplicateMap.get(course.google_place_id);
      if (existing) {
        return { ...course, id: existing.id, source: 'database' };
      }

      return course;
    });

    return { data: mergedCourses, error: null };

  } catch (error) {
    console.error('Exception saving external courses:', error);
    return { data: courses, error };
  }
};

/**
 * Progressive search - returns results in phases for better UX
 * Phase 1: Local DB results from golf_clubs table (instant)
 * Phase 2: External Google Places results with skeleton loading while saving to database
 *
 * @param {string} searchTerm - Search term
 * @param {function} onUpdate - Callback called whenever results change
 *   - onUpdate({ results, isLoading, loadingCount })
 * @returns {Object} - Final result object with all data
 */
export const searchCombinedGolfCoursesProgressive = async (searchTerm, onUpdate) => {
  // Local-only search. Google Places is no longer hit during typing - if the
  // local DB has nothing for the user, the autocomplete falls through to the
  // explicit "Add new course" flow (which uses the lookup-golf-course edge
  // function and saves the result back to golf_clubs for next time).
  try {
    if (!searchTerm || searchTerm.trim().length < 3) {
      return { data: [], error: null };
    }

    const trimmedTerm = searchTerm.trim();
    let localResults = [];

    try {
      // search_golf_clubs RPC handles prefix + fuzzy + city/state matching.
      const { data: rpcResults, error: rpcError } = await supabase
        .rpc('search_golf_clubs', { search_term: trimmedTerm });

      if (!rpcError && rpcResults && rpcResults.length > 0) {
        // Fetch full records by ID to get all fields the autocomplete needs.
        const matchedIds = rpcResults.map(r => r.id);
        const { data: fullRecords, error: fullError } = await supabase
          .from('golf_clubs')
          .select('id, name, course_name, latitude, longitude, address, city, state, country, country_code, google_place_id, golfnow_facility_id, booking_url')
          .in('id', matchedIds);

        if (!fullError && fullRecords) {
          // Preserve RPC relevance ordering.
          const recordMap = new Map(fullRecords.map(r => [r.id, r]));
          localResults = rpcResults
            .map(rpc => recordMap.get(rpc.id))
            .filter(Boolean)
            .map(course => ({
              ...course,
              source: 'database',
              has_coordinates: !!(course.latitude && course.longitude)
            }));
        }
      }
    } catch (e) {
      console.warn('Local search failed:', e);
    }

    if (onUpdate) {
      onUpdate({
        results: localResults,
        isLoading: false,
        isStandardising: false,
        loadingCount: 0
      });
    }

    return { data: localResults, error: null };
  } catch (error) {
    console.error('Exception in searchCombinedGolfCoursesProgressive:', error);
    return { data: null, error };
  }
};

